import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { normalizeBareUrl, isInList } from './link-unfurl-paste';
import { isYoutubeUrl } from './youtube-paste';

const linkUnfurlInputKey = new PluginKey('linkUnfurlInput');

/**
 * Typing trigger: when the user presses Space or Enter and the current
 * paragraph contains ONLY a bare URL (nothing else), convert it into a
 * linkUnfurl card. Mirrors the paste rule for typed URLs.
 *
 * Guards (matching the paste rule + autolink boundary):
 *   - the paragraph's entire text must be a single URL (so a URL inside a
 *     sentence stays an inline autolink — this handler defers)
 *   - not inside a list
 *   - not a YouTube URL (those embed as video via YoutubePaste / the Youtube
 *     extension; typed YouTube is left alone for consistency with paste)
 *
 * Everything else (X/Twitter, Figma, generic sites) becomes a generic card —
 * there are no provider-specific branches yet.
 */
export const LinkUnfurlInput = Extension.create({
  name: 'linkUnfurlInput',

  addProseMirrorPlugins() {
    const editor = this.editor;
    return [
      new Plugin({
        key: linkUnfurlInputKey,
        props: {
          handleKeyDown(view: EditorView, event: KeyboardEvent) {
            if (event.key !== ' ' && event.key !== 'Enter') return false;

            const { state } = view;
            const { $from, empty } = state.selection;
            if (!empty) return false;
            if (isInList(state)) return false;

            const parent = $from.parent;
            if (parent.type.name !== 'paragraph') return false;

            const text = parent.textContent.trim();
            const normalized = normalizeBareUrl(text);
            if (!normalized) return false;
            // Leave YouTube URLs for the video embed.
            if (isYoutubeUrl(text) || isYoutubeUrl(normalized)) return false;
            if (!state.schema.nodes.linkUnfurl) return false;

            // Replace the whole paragraph with a card node, in one transaction
            // (single undo step). The paragraph spans [start-1, end+1] in doc
            // coords (its open/close tokens); use the resolved boundaries.
            const start = $from.start();
            const from = start - 1;
            const to = start + parent.content.size + 1;
            editor
              .chain()
              .deleteRange({ from, to })
              .insertContentAt(from, { type: 'linkUnfurl', attrs: { href: normalized } })
              .run();

            event.preventDefault();
            return true;
          },
        },
      }),
    ];
  },
});
